/**
 * QboDocsTab — factures clients (Invoice) ou factures fournisseurs (Bill) QuickBooks, copie locale : statut (ouverte · en retard · payée),
 * tiers, période, recherche ; totaux ; ligne dépliée = lignes de la facture (article / compte, quantité, prix, montant, code de taxe).
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Receipt, Search } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Empty, Pill, errMsg, inputCls, money } from "../../comptabilite/_shared";
import { Num, TH, THead, Table } from "../../comptabilite/reports/_bits";
import { DOC_CFG, QBO_STATUS, qboBase } from "./_qbo";

const dateCls = "border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white";
const FILTERS = [["", "Toutes"], ["open", "Ouvertes"], ["overdue", "En retard"], ["paid", "Payées"]];

function DocLines({ masterId, kind, id }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.get(`${qboBase(masterId)}/entities/${kind}/${id}`).then((r) => setDoc(r.data)).catch(() => setDoc({ lines: [] })); }, [masterId, kind, id]);
  if (!doc) return <div className="p-3 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>;
  return (
    <div className="bg-slate-50/60 px-4 py-2 text-xs" data-testid={`qbo-doc-lines-${id}`}>
      {doc.memo && <p className="text-slate-600 mb-1 italic">{doc.memo}</p>}
      {(doc.lines || []).length === 0 ? <p className="text-slate-400">Aucune ligne détaillée.</p> : (
        <Table>
          <THead><TH>{kind === "invoices" ? "Article / service" : "Compte / article"}</TH><TH>Description</TH><TH right className="w-20">Qté</TH><TH right className="w-28">Prix</TH><TH right className="w-28">Montant</TH><TH className="w-20">Taxe</TH></THead>
          <tbody className="divide-y">
            {doc.lines.map((l, i) => (
              <tr key={i}><td className="px-3 py-1">{l.item}</td><td className="px-3 py-1">{l.description}</td><td className="px-3 py-1 text-right font-mono">{l.qty ?? ""}</td><td className="px-3 py-1 text-right font-mono">{l.rate != null ? money(l.rate) : ""}</td><Num v={l.amount} blankZero={false} /><td className="px-3 py-1 font-mono whitespace-nowrap">{l.tax_code}</td></tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default function QboDocsTab({ masterId, kind }) {
  const cfg = DOC_CFG[kind];
  const [f, setF] = useState({ status: "open", party: "", q: "", date_from: "", date_to: "" });
  const [parties, setParties] = useState([]);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get(`${qboBase(masterId)}/entities/${cfg.partyKind}`).then((r) => setParties(r.data.items || [])).catch(() => setParties([])); }, [masterId, cfg.partyKind]);
  const load = useCallback(async () => {
    try { const r = await api.get(`${qboBase(masterId)}/entities/${kind}`, { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }); setData(r.data); }
    catch (e) { toast.error(errMsg(e)); setData({ items: [], totals: {} }); }
  }, [masterId, kind, f]);
  useEffect(() => { const t = setTimeout(load, f.q ? 250 : 0); return () => clearTimeout(t); }, [load, f.q]);
  const bs = data?.totals?.by_status;

  return (
    <div className="space-y-3" data-testid={`qbo-docs-${kind}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          {FILTERS.map(([v, l]) => <button key={v} onClick={() => setF({ ...f, status: v })} className={`px-3 py-1.5 text-xs font-semibold ${f.status === v ? "bg-[#2CA01C] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`qbo-docs-filter-${v || "all"}`}>{l}</button>)}
        </div>
        <select value={f.party} onChange={(e) => setF({ ...f, party: e.target.value })} className={`${dateCls} max-w-xs`} data-testid="qbo-docs-party">
          <option value="">Tous les {cfg.partyLabel.toLowerCase()}s</option>
          {parties.map((p) => <option key={p.qbo_id} value={p.qbo_id}>{p.name}</option>)}
        </select>
        <span className="text-xs text-slate-500">Du</span><input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={dateCls} data-testid="qbo-docs-from" />
        <span className="text-xs text-slate-500">au</span><input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={dateCls} data-testid="qbo-docs-to" />
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="N°, tiers, mémo" className={`${inputCls} pl-9`} data-testid="qbo-docs-search" />
        </div>
      </div>
      {bs && (
        <div className="flex gap-2 flex-wrap text-xs" data-testid="qbo-docs-totals">
          <Pill cls={QBO_STATUS.open.cls}>{bs.open.count} ouverte(s) · {money(bs.open.balance)}</Pill>
          <Pill cls={QBO_STATUS.overdue.cls}>{bs.overdue.count} en retard · {money(bs.overdue.balance)}</Pill>
          <Pill cls={QBO_STATUS.paid.cls}>{bs.paid.count} payée(s)</Pill>
          <span className="text-slate-500 ml-auto">{data.totals.count} facture(s) · total <strong className="font-mono">{money(data.totals.total)}</strong> · {cfg.balanceLabel.toLowerCase()} <strong className="font-mono">{money(data.totals.balance)}</strong></span>
        </div>
      )}
      <div className="bg-white border rounded-xl overflow-hidden">
        {!data ? <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          : data.items.length === 0 ? <Empty icon={Receipt} text={`Aucune ${cfg.label.toLowerCase()} pour ces filtres — actualisez les données QuickBooks si besoin.`} testId="qbo-docs-empty" />
            : (
              <Table>
                <THead><TH className="w-6"></TH><TH className="w-24">Date</TH><TH className="w-24">N°</TH><TH>{cfg.partyLabel}</TH><TH className="w-24 hidden md:table-cell">Échéance</TH><TH right className="w-16 hidden md:table-cell">Retard</TH><TH right className="w-28">Total</TH><TH right className="w-24 hidden lg:table-cell">Taxes</TH><TH right className="w-28">Solde</TH><TH className="w-24">Statut</TH></THead>
                <tbody className="divide-y">
                  {data.items.map((d) => (
                    <DocRow key={d.qbo_id} d={d} open={open === d.qbo_id} toggle={() => setOpen(open === d.qbo_id ? null : d.qbo_id)} masterId={masterId} kind={kind} />
                  ))}
                </tbody>
              </Table>
            )}
      </div>
    </div>
  );
}

function DocRow({ d, open, toggle, masterId, kind }) {
  const st = QBO_STATUS[d.status] || { label: d.status, cls: "" };
  return (
    <>
      <tr className="hover:bg-emerald-50/40 cursor-pointer" onClick={toggle} data-testid={`qbo-doc-row-${d.qbo_id}`}>
        <td className="pl-3 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="px-3 py-2 font-mono text-xs">{d.txn_date}</td><td className="px-3 py-2 font-mono text-xs">{d.doc_number || "—"}</td>
        <td className="px-3 py-2 text-slate-800">{d.party_name}</td>
        <td className="px-3 py-2 font-mono text-xs text-slate-500 hidden md:table-cell">{d.due_date || "—"}</td>
        <td className={`px-3 py-2 text-right text-xs hidden md:table-cell ${d.days_late > 0 ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{d.days_late > 0 ? `${d.days_late} j` : ""}</td>
        <Num v={d.total} blankZero={false} /><Num v={d.tax} className="hidden lg:table-cell text-slate-500" /><Num v={d.balance} className={d.balance > 0 ? "font-semibold" : ""} />
        <td className="px-3 py-2"><Pill cls={st.cls}>{st.label}</Pill></td>
      </tr>
      {open && <tr><td colSpan={10} className="p-0"><DocLines masterId={masterId} kind={kind} id={d.qbo_id} /></td></tr>}
    </>
  );
}
